/**
 * CSV Field Definitions
 *
 * Central registry of all fields available for CSV export templates.
 * Each field knows how to extract its value from a payrollRuns employee object.
 *
 * Used by:
 * - CsvExportLayoutPage.js (Settings: template editor)
 * - PayrollList.js (CSV generation from saved templates)
 */

import { calculateEmployeeFinancials } from '../utils/payrollCalculations';

/**
 * Sum a deduction value that may be a flat number or per-month object.
 * Mirrors the private sumDeduction() in payrollCalculations.js.
 */
function sumDeduction(deduction) {
  if (typeof deduction === 'object' && deduction !== null && !Array.isArray(deduction)) {
    return Object.values(deduction).reduce((sum, val) => sum + (val || 0), 0);
  }
  return deduction || 0;
}

/**
 * All available CSV fields.
 *
 * Each field:
 *   id       — unique key, stored in Firestore template
 *   label    — human-readable column header (used in CSV + settings UI)
 *   category — grouping for the settings editor
 *   field    — short technical path shown in the editor (e.g. "earnings.basic")
 *   extract  — (employee) => value  — pulls the cell value from a payrollRuns employee
 */
export const CSV_FIELDS = [
  // ─── Employee Info ───
  {
    id: 'employeeId',
    label: 'Employee ID',
    category: 'Employee',
    field: 'employeeId',
    extract: (emp) => emp.employeeId || emp.employeeCode || '',
  },
  {
    id: 'fullName',
    label: 'Employee Name',
    category: 'Employee',
    field: 'fullName',
    extract: (emp) =>
      emp.employeeName ||
      `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim() ||
      '',
  },
  {
    id: 'firstName',
    label: 'First Name',
    category: 'Employee',
    field: 'personalInfo.firstName',
    extract: (emp) => emp.firstName || emp.personalInfo?.firstName || '',
  },
  {
    id: 'lastName',
    label: 'Last Name',
    category: 'Employee',
    field: 'personalInfo.lastName',
    extract: (emp) => emp.lastName || emp.personalInfo?.lastName || '',
  },
  {
    id: 'middleName',
    label: 'Middle Name',
    category: 'Employee',
    field: 'personalInfo.middleName',
    extract: (emp) => emp.middleName || emp.personalInfo?.middleName || '',
  },
  {
    id: 'mobile',
    label: 'Mobile Number',
    category: 'Employee',
    field: 'personalInfo.mobile',
    extract: (emp) => emp.personalInfo?.mobile || '',
  },
  {
    id: 'email',
    label: 'Email',
    category: 'Employee',
    field: 'personalInfo.email',
    extract: (emp) => emp.personalInfo?.email || '',
  },
  {
    id: 'department',
    label: 'Department',
    category: 'Employee',
    field: 'department',
    extract: (emp) => emp.employment?.department || '',
  },
  {
    id: 'accountNumber',
    label: 'Account Number',
    category: 'Employee',
    field: 'banking.accountNumber',
    // Read top-level first (legacy + fixed server runs); fall back to the
    // nested pii.banking shape that pre-fix server-compute runs saved, so
    // already-saved runs don't export blank. See computePayrollEligibility
    // projectDetail.
    extract: (emp) => emp.banking?.accountNumber || emp.pii?.banking?.accountNumber || '',
  },
  {
    id: 'accountName',
    label: 'Account Name',
    category: 'Employee',
    field: 'banking.accountName',
    extract: (emp) => emp.banking?.accountName || emp.pii?.banking?.accountName || '',
  },

  // ─── Earnings ───
  {
    id: 'basicPay',
    label: 'Basic Pay',
    category: 'Earnings',
    field: 'earnings.basic',
    extract: (emp) => (emp.earnings?.basic || 0).toFixed(2),
  },
  {
    id: 'overtimePay',
    label: 'Overtime Pay',
    category: 'Earnings',
    field: 'earnings.overtime',
    extract: (emp) => (emp.earnings?.overtime || 0).toFixed(2),
  },
  {
    id: 'nightDiff',
    label: 'Night Differential',
    category: 'Earnings',
    field: 'earnings.nightDiff',
    extract: (emp) => (emp.earnings?.nightDiff || 0).toFixed(2),
  },
  {
    id: 'holidayPay',
    label: 'Holiday Pay',
    category: 'Earnings',
    field: 'earnings.holiday',
    extract: (emp) => (emp.earnings?.holiday || 0).toFixed(2),
  },
  {
    id: 'restDayPay',
    label: 'Rest Day Premium',
    category: 'Earnings',
    field: 'earnings.restDay',
    extract: (emp) => (emp.earnings?.restDay || 0).toFixed(2),
  },
  {
    id: 'pieceRate',
    label: 'Piece Rate',
    category: 'Earnings',
    field: 'pieceRate',
    extract: (emp) => (emp.pieceRate || 0).toFixed(2),
  },

  // ─── Deductions ───
  {
    id: 'sssDeduction',
    label: 'SSS',
    category: 'Deductions',
    field: 'deductions.sss',
    extract: (emp) => sumDeduction(emp.deductions?.sss).toFixed(2),
  },
  {
    id: 'philhealthDeduction',
    label: 'PhilHealth',
    category: 'Deductions',
    field: 'deductions.philhealth',
    extract: (emp) => sumDeduction(emp.deductions?.philhealth).toFixed(2),
  },
  {
    id: 'pagibigDeduction',
    label: 'Pag-IBIG',
    category: 'Deductions',
    field: 'deductions.pagibig',
    extract: (emp) => sumDeduction(emp.deductions?.pagibig).toFixed(2),
  },
  {
    id: 'taxDeduction',
    label: 'Tax',
    category: 'Deductions',
    field: 'deductions.tax',
    extract: (emp) => sumDeduction(emp.deductions?.tax).toFixed(2),
  },
  // No 'Late Deduction' column: late is tracked as a flag only (CL-002), so
  // nothing ever writes a non-zero `deductions.late` and the column exported
  // 0.00 for every employee forever. Don't re-add it without a real late
  // deduction behind it (BL-007).
  {
    id: 'undertimeDeduction',
    label: 'Undertime Deduction',
    category: 'Deductions',
    field: 'deductions.undertime',
    extract: (emp) => (emp.deductions?.undertime || 0).toFixed(2),
  },
  {
    // Absence is the M1 deduct-down bucket for monthly-paid employees (confirmed
    // unpaid days); always 0 for daily. Reduces gross like late/undertime, so it
    // belongs in the itemized deductions or the columns won't foot to net pay.
    id: 'absenceDeduction',
    label: 'Absence Deduction',
    category: 'Deductions',
    field: 'deductions.absence',
    extract: (emp) => (emp.deductions?.absence || 0).toFixed(2),
  },

  // ─── Calculated Totals ───
  {
    id: 'grossPay',
    label: 'Gross Pay',
    category: 'Totals',
    field: 'calculated',
    extract: (emp) => calculateEmployeeFinancials(emp).gross.toFixed(2),
  },
  {
    // PRD_PayrollFigureAlignment C3 / Amendment A1 — the id and the value stay
    // government-only (saved templates store the id); the label is the header text
    // written into the exported file, and "Total Deductions" now means everything
    // withheld on the payslip.
    id: 'totalDeductions',
    label: 'Government Contributions',
    category: 'Totals',
    field: 'calculated',
    extract: (emp) => calculateEmployeeFinancials(emp).totalDeductions.toFixed(2),
  },
  {
    id: 'netAdjustments',
    label: 'Net Adjustments',
    category: 'Totals',
    field: 'calculated',
    extract: (emp) => calculateEmployeeFinancials(emp).netAdjustments.toFixed(2),
  },
  {
    // PRD_PayrollFigureAlignment C2/C3 — everything withheld (government + loans +
    // manual deductions), as the individual payslip's "Total Deductions" prints it.
    // A NEW id: `totalDeductions` above keeps its government-only meaning, because
    // it lives in customers' saved templates and changing it would alter exports of
    // runs already approved and paid. Not in any default layout.
    id: 'totalWithheld',
    label: 'Total Withheld',
    category: 'Totals',
    field: 'calculated',
    extract: (emp) => calculateEmployeeFinancials(emp).totalWithheld.toFixed(2),
  },
  {
    // PRD_LoansManagement C8e — the run's loan installments, its own figure so
    // `totalDeductions` keeps meaning government-only. Not in any default layout.
    id: 'loanDeductions',
    label: 'Loan Deductions',
    category: 'Totals',
    field: 'calculated',
    extract: (emp) => calculateEmployeeFinancials(emp).totalLoans.toFixed(2),
  },
  {
    id: 'netPay',
    label: 'Net Pay',
    category: 'Totals',
    field: 'calculated',
    // Floor at ₱0 so the disbursement file can never carry a negative amount. A negative
    // net means the employee owes the company; finalize is hard-blocked on negative net
    // in ProcessPayroll, so this is a belt-and-suspenders guard for any non-finalized or
    // legacy export path. Reconciliation columns (gross/deductions/netAdjustments) keep
    // their real signed values; only the bank-file Net Pay is floored.
    extract: (emp) => Math.max(0, calculateEmployeeFinancials(emp).net).toFixed(2),
  },
];

/** Quick lookup: field id → field definition */
export const CSV_FIELD_MAP = Object.fromEntries(CSV_FIELDS.map(f => [f.id, f]));

/** All unique category names, in display order */
export const CSV_CATEGORIES = ['Employee', 'Earnings', 'Deductions', 'Totals'];

/** Fields that trigger the Excel account-number warning when enabled */
export const ACCOUNT_WARNING_FIELDS = ['accountNumber', 'accountName'];

/** Fields the "Preserve leading zeros" option wraps for Excel */
const LEADING_ZERO_FIELDS = ['accountNumber', 'mobile', 'employeeId'];

/** Maximum number of saved templates per company */
export const MAX_TEMPLATES = 10;

/**
 * Default columns for a new template.
 * Matches the current hardcoded CSV: Account Number, Net Pay, Last Name, First Name, Middle Name.
 * All other fields are present but disabled.
 */
const DEFAULT_ENABLED = ['accountNumber', 'netPay', 'lastName', 'firstName', 'middleName'];

export function getDefaultColumns() {
  const enabled = DEFAULT_ENABLED.map(id => ({ id, enabled: true }));
  const disabled = CSV_FIELDS
    .filter(f => !DEFAULT_ENABLED.includes(f.id))
    .map(f => ({ id: f.id, enabled: false }));
  return [...enabled, ...disabled];
}

/**
 * The field definitions a template will actually export, in column order.
 *
 * Filters on BOTH `enabled` and a live definition — an id whose definition has
 * since been retired (e.g. 'lateDeduction', BL-007) contributes no header and no
 * cell. Shared so the download modal's column count and the generated file can
 * never disagree (BL-084).
 *
 * @param {Object} template - Template object with { columns }
 * @returns {Array} CSV field definitions; disabled and retired ids removed
 */
export function getUsableFields(template) {
  return (template?.columns || [])
    .filter(c => c.enabled)
    .map(c => CSV_FIELD_MAP[c.id])
    .filter(Boolean);
}

/**
 * Generate CSV content from employees using a template's column list.
 *
 * @param {Array}  employees - Employee objects from payrollRuns
 * @param {Object} template  - Template object with { columns, preserveLeadingZeros }
 * @returns {string} CSV string ready for download
 */
export function generateCsvContent(employees, template) {
  const { preserveLeadingZeros = false } = template;
  const fieldDefs = getUsableFields(template);

  const headers = fieldDefs.map(f => f.label);

  // Sort by surname (then first name) so the export is alphabetical.
  // Names live under personalInfo on payroll-run employees; the leading
  // e.lastName fallback just mirrors the column extractor and is rarely hit.
  const surnameOf = (e) => (e.lastName || e.personalInfo?.lastName || '').toLowerCase();
  const firstNameOf = (e) => (e.firstName || e.personalInfo?.firstName || '').toLowerCase();
  const sortedEmployees = [...(employees || [])].sort((a, b) =>
    surnameOf(a).localeCompare(surnameOf(b)) || firstNameOf(a).localeCompare(firstNameOf(b))
  );

  const rows = sortedEmployees.map(emp =>
    fieldDefs.map(f => {
      const value = f.extract(emp);
      // Wrap zero-padded numbers (account, mobile, employee ID) in Excel-formula syntax so Excel/Sheets
      // preserve leading zeros instead of coercing to a number.
      if (preserveLeadingZeros && LEADING_ZERO_FIELDS.includes(f.id) && value) {
        return `="${value}"`;
      }
      return value;
    })
  );

  const escapeCsvCell = (cell) => {
    const str = String(cell);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  return [
    headers.map(escapeCsvCell).join(','),
    ...rows.map(row => row.map(escapeCsvCell).join(','))
  ].join('\n');
}

/**
 * Check if a template includes any account-number fields.
 *
 * @param {Array} columns - Array of { id, enabled }
 * @returns {boolean}
 */
export function templateHasAccountFields(columns) {
  return (columns || []).some(
    c => c.enabled && ACCOUNT_WARNING_FIELDS.includes(c.id)
  );
}
